import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, signal } from '@angular/core';
import { PopupBaseComponent } from '../../../../../shared/components/popup/popup-base/popup-base.component';
import { ApplicationStore, ApplicationStoreType } from '../../../../../core/store/application.store';
import { PropertiesService } from '../../../service/properties.service';
import { AddPropertyPopupModel } from './add-property-popup.model';
import {
    GuestlinePmsSetup,
    MewsPmsSetup,
    PmsSetup,
    PmsType,
    ShijiPmsSetup,
    StayNTouchPmsSetup,
} from '../../../types/pms';
import {
    PropertyDetailsStepComponent,
    PropertyDetailsValue,
} from '../../steps/property-details-step/property-details-step.component';
import { SelectPmsStepComponent } from '../../steps/select-pms-step/select-pms-step.component';
import { ConnectStepComponent } from '../../steps/connect-step/connect-step.component';
import { PmsStatusMsg, Property } from '../../../types/property';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { forkJoin, of } from 'rxjs';
import { LayoutStore, LayoutStoreType } from '../../../../../core/store/layout.store';
import { OrganizationPrivilege } from '../../../../../shared/types/organization';
import { Toast } from '../../../../../shared/types/toast';
import { uuidv4 } from '../../../../../shared/utils/unique-id-utils';

@Component({
    selector: 'juyo-add-property-popup',
    styleUrl: './add-property-popup.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [PopupBaseComponent, PropertyDetailsStepComponent, SelectPmsStepComponent, ConnectStepComponent],
    template: `
        <juyo-popup-base
            [popupId]="model().id"
            [showHeader]="true"
            [showFooter]="false"
            [maxWidth]="'700px'"
            [autoClose]="false"
            (closeRequest)="onCloseRequest()"
        >
            <span slot="header">Add Property</span>

            <div slot="body" class="wizard-body">
                <div class="step-indicator">
                    <div
                        class="step-pill"
                        [class.active]="currentStep() === AddPropertyStep.PropertyDetails"
                        [class.completed]="
                            currentStep() === AddPropertyStep.SelectPms || currentStep() === AddPropertyStep.Connect
                        "
                    >
                        1. Property details
                    </div>
                    @if (canSetupPms()) {
                        <div
                            class="step-pill"
                            [class.active]="currentStep() === AddPropertyStep.SelectPms"
                            [class.completed]="currentStep() === AddPropertyStep.Connect"
                        >
                            2. Select PMS
                        </div>
                        <div class="step-pill" [class.active]="currentStep() === AddPropertyStep.Connect">
                            3. Connect
                        </div>
                    }
                </div>

                @if (currentStep() === AddPropertyStep.PropertyDetails) {
                    <juyo-property-details-step
                        [saving]="isSaving()"
                        [createdProperty]="createdProperty()"
                        (continue)="onPropertyDetailsContinue($event)"
                        (cancel)="onCancel()"
                    />
                }
                @if (canSetupPms() && currentStep() === AddPropertyStep.SelectPms) {
                    <juyo-select-pms-step
                        [showBack]="true"
                        (continue)="onSelectPmsContinue($event)"
                        (back)="onBack()"
                        (cancel)="onCancel()"
                        (skip)="onSkip()"
                    />
                }
                @if (canSetupPms() && currentStep() === AddPropertyStep.Connect) {
                    <juyo-connect-step
                        [pmsType]="selectedPmsType()!"
                        [saving]="isSaving()"
                        [propertyName]="createdProperty()?.name ?? ''"
                        (submit)="onConnectSubmit($event)"
                        (back)="onBack()"
                        (cancel)="onCancel()"
                        (skip)="onSkip()"
                    />
                }
            </div>
        </juyo-popup-base>
    `,
})
export class AddPropertyPopupComponent {
    public model = input.required<AddPropertyPopupModel>();

    protected readonly AddPropertyStep = AddPropertyStep;
    private applicationStore: ApplicationStoreType = inject(ApplicationStore);
    private layoutStore: LayoutStoreType = inject(LayoutStore);
    private propertiesService = inject(PropertiesService);
    private destroyRef = inject(DestroyRef);

    protected currentStep = signal<AddPropertyStep>(AddPropertyStep.PropertyDetails);
    protected createdProperty = signal<Property | null>(null);
    protected isSaving = signal(false);
    protected selectedPmsType = signal<PmsType | null>(null);
    protected canSetupPms = computed<boolean>(() =>
        this.applicationStore.hasPrivilege(OrganizationPrivilege.OrgConnectDataSources),
    );

    protected onPropertyDetailsContinue(value: PropertyDetailsValue): void {
        const existing = this.createdProperty();
        const organization = this.applicationStore.selectedOrganization();

        this.isSaving.set(true);
        if (!existing) {
            this.propertiesService
                .addProperty(organization!.id, value.name, value.rooms, value.cityData!)
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe((response) => {
                    const property: Property = {
                        id: response.id,
                        code: response.code,
                        name: value.name,
                        city: value.cityData!.city,
                        countryCode: value.cityData!.countryCode,
                        currency: value.cityData!.currencyCode,
                        timezone: value.cityData!.timezone,
                        rooms: value.rooms,
                        pms: undefined,
                        status: {
                            msg: PmsStatusMsg.NotConnected,
                            lastUpdate: new Date().toISOString(),
                        },
                        features: null,
                        addons: null,
                        dataSources: [],
                    };
                    this.createdProperty.set(property);
                    this.isSaving.set(false);
                    if (this.canSetupPms()) {
                        this.currentStep.set(AddPropertyStep.SelectPms);
                    } else {
                        this.closeAndFinish(property);
                    }
                });
        } else {
            const cityData = value.cityData!;
            const detailsChanged =
                value.name !== existing.name ||
                cityData.city !== existing.city ||
                cityData.countryCode !== existing.countryCode ||
                cityData.currencyCode !== existing.currency ||
                cityData.timezone !== existing.timezone;
            const detailsUpdate$ = detailsChanged
                ? this.propertiesService.updateHotelDetails(
                      organization!.id,
                      existing.code,
                      value.name,
                      cityData.city,
                      cityData.currencyCode,
                      cityData.countryCode,
                      cityData.timezone,
                  )
                : of(null);
            const capacityUpdate$ =
                value.rooms !== existing.rooms
                    ? this.propertiesService.updateHotelCapacity(organization!.id, existing.code, value.rooms)
                    : of(null);

            forkJoin([detailsUpdate$, capacityUpdate$])
                .pipe(takeUntilDestroyed(this.destroyRef))
                .subscribe(() => {
                    this.createdProperty.update((p) =>
                        p
                            ? {
                                  ...p,
                                  name: value.name,
                                  rooms: value.rooms,
                                  city: cityData.city,
                                  countryCode: cityData.countryCode,
                                  currency: cityData.currencyCode,
                                  timezone: cityData.timezone,
                              }
                            : p,
                    );
                    this.isSaving.set(false);
                    if (this.canSetupPms()) {
                        this.currentStep.set(AddPropertyStep.SelectPms);
                    } else {
                        const updated = this.createdProperty();
                        if (updated) this.closeAndFinish(updated);
                    }
                });
        }
    }

    protected onSelectPmsContinue(pmsType: PmsType): void {
        this.selectedPmsType.set(pmsType);
        this.currentStep.set(AddPropertyStep.Connect);
    }

    protected onConnectSubmit(setup: PmsSetup): void {
        const property = this.createdProperty();
        const organization = this.applicationStore.selectedOrganization();
        const user = this.applicationStore.signedInUser();

        if (organization && property && user) {
            if (setup.type === PmsType.Mews) {
                const { apiToken } = setup as MewsPmsSetup;
                this.isSaving.set(true);
                this.propertiesService
                    .configureMews(organization.id, property.code, setup.type, apiToken)
                    .subscribe(() => {
                        property.pms = PmsType.Mews;
                        property.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.closeAndFinish(property);
                        this.isSaving.set(false);
                    });
            } else if (setup.type === PmsType.Guestline) {
                const { siteId, groupId } = setup as GuestlinePmsSetup;
                this.propertiesService
                    .configureGuestline(organization.id, property.code, setup.type, siteId, groupId)
                    .subscribe(() => {
                        property.pms = PmsType.Guestline;
                        property.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.closeAndFinish(property);
                        this.isSaving.set(false);
                    });
            } else if (setup.type === PmsType.Shiji) {
                const { tenantName, propertyId, password, username } = setup as ShijiPmsSetup;
                this.propertiesService
                    .configureShiji(
                        organization.id,
                        property.code,
                        setup.type,
                        username,
                        tenantName,
                        password,
                        propertyId,
                    )
                    .subscribe(() => {
                        property.pms = PmsType.Shiji;
                        property.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.closeAndFinish(property);
                        this.isSaving.set(false);
                    });
            } else if (setup.type === PmsType.StayNTouch) {
                const { baseUrl, tokenUrl, hotelId, clientId, clientSecret } = setup as StayNTouchPmsSetup;
                this.isSaving.set(true);
                this.propertiesService
                    .configureSNT(
                        organization.id,
                        property.code,
                        setup.type,
                        hotelId,
                        clientId,
                        clientSecret,
                        baseUrl,
                        tokenUrl,
                    )
                    .subscribe(() => {
                        property.pms = PmsType.StayNTouch;
                        property.status = {
                            msg: PmsStatusMsg.Connecting,
                            lastUpdate: new Date().toISOString(),
                        };
                        this.closeAndFinish(property);
                        this.isSaving.set(false);
                    });
            } else {
                this.isSaving.set(true);
                this.propertiesService
                    .configurePms(organization, property, setup, user)
                    .pipe(takeUntilDestroyed(this.destroyRef))
                    .subscribe({
                        next: () => {
                            property.status = {
                                msg: PmsStatusMsg.Connecting,
                                lastUpdate: new Date().toISOString(),
                            };
                            this.closeAndFinish(property);
                            this.isSaving.set(false);
                        },
                        error: () => {
                            this.layoutStore.addTimedToast(
                                new Toast(uuidv4(), ['Something went wrong, please try again.']),
                            );
                            this.isSaving.set(false);
                        },
                    });
            }
        }
    }

    protected onBack(): void {
        if (this.currentStep() === AddPropertyStep.SelectPms) {
            this.currentStep.set(AddPropertyStep.PropertyDetails);
        } else if (this.currentStep() === AddPropertyStep.Connect) {
            this.currentStep.set(AddPropertyStep.SelectPms);
        }
    }

    protected onCancel(): void {
        this.onCloseRequest();
    }

    protected onCloseRequest(): void {
        const property = this.createdProperty();
        if (property) {
            this.closeAndFinish(property);
        } else {
            this.layoutStore.closePopup(this.model().id);
        }
    }

    protected onSkip(): void {
        const property = this.createdProperty();
        if (property) {
            this.closeAndFinish(property);
        }
    }

    private closeAndFinish(property: Property): void {
        this.model().onAdded(property);
        this.layoutStore.closePopup(this.model().id);
    }
}

enum AddPropertyStep {
    PropertyDetails = 'PROPERTY_DETAILS',
    SelectPms = 'SELECT_PMS',
    Connect = 'CONNECT',
}
